import { AxiosError } from "axios";
import memoize from "memoizee";

import { Operation, OperationsDataEnvelope } from "../../../models/operations";
import { QueryStatus, consolidateStatuses } from "../../../services/query";
import { isNotNullOrUndefined } from "../../../services/util";
import {
  numberOfOperationsListsPossiblyActiveAtTheSameTime,
  sortedListOfOperations,
} from "../../../services/helpers/refinements";

/**
 * Compute the combined operations data envelope.
 *
 * This is only for use in useOperationsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeCombinedData = memoize(
  (
    currentOperationData: Operation | undefined,
    operationsData: OperationsDataEnvelope | undefined,
  ) => {
    if (!currentOperationData || !operationsData) {
      return undefined;
    }

    const combinedOperationsById = {
      ...operationsData.operationsById,
      [currentOperationData.id]: currentOperationData,
    };

    const combinedOperations = sortedListOfOperations(
      // Using combinedOperationsById to naturally dedupe
      Object.values(combinedOperationsById).filter(isNotNullOrUndefined),
    );

    const combinedData: OperationsDataEnvelope = {
      operations: combinedOperations,
      operationsById: combinedOperationsById,
      totalOperationsCount: operationsData.totalOperationsCount,
    };

    return combinedData;
  },
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[operations.query.helpers] computeCombinedData",
  },
);

/**
 * Compute the combined operations status.
 *
 * This is only for use in useOperationsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeCombinedStatus = memoize(
  (currentOperationStatus: QueryStatus, operationsStatus: QueryStatus) => {
    return consolidateStatuses(currentOperationStatus, operationsStatus);
  },
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[operations.query.helpers] computeCombinedStatus",
  },
);

/**
 * Compute the combined operations error.
 *
 * This is only for use in useOperationsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeCombinedError = (
  currentOperationError: AxiosError | null,
  operationsError: AxiosError | null,
): AxiosError | null => {
  return currentOperationError || operationsError;
};

/**
 * Compute the combined operations envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in useOperationsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeEnvelope = memoize(
  (
    data: OperationsDataEnvelope | undefined,
    status: QueryStatus,
    error: AxiosError | null,
  ) => {
    return { data, status, error };
  },
  {
    max: numberOfOperationsListsPossiblyActiveAtTheSameTime,
    profileName: "[operations.query.helpers] computeEnvelope",
  },
);
